"use client";

import * as React from "react";
import { CircleCheck, ChevronDown, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
const stages = [{
  id: "01",
  title: "Transformation Discovery Workshop",
  description: "Comprehensive analysis of your current operations, identifying high-impact AI opportunities and technical readiness for agentic transformation.",
  deliverables: ["Workflow Bottleneck Map", "Strategic Opportunity Report"]
}, {
  id: "02",
  title: "Strategy & Roadmap",
  description: "Leveraging decades of expertise to design a bespoke AI integration strategy that aligns with your specific long-term business goals.",
  deliverables: ["Agentic Solution Blueprint", "ROI Projection Model"]
}, {
  id: "03",
  title: "Iterative Integration",
  description: "Seamless deployment of custom AI solutions into your existing operations, ensuring minimal disruption and maximum employee adoption.",
  deliverables: ["Pilot Implementation", "Custom API Integrations", "Employee Training Modules", "Data Security Protocols"]
}, {
  id: "04",
  title: "Continuous Evolution",
  description: "Ongoing performance monitoring and strategic optimization to ensure your AI systems scale effectively as your business expands.",
  deliverables: ["Performance Analytics Dashboard", "Model Tuning Report", "Ongoing Advisory Support"]
}];
export default function DeploymentStages() {
  return <section data-section-id="110011" id="the-focuspath-process" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-[128px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-chart-2/10 rounded-full blur-[128px] pointer-events-none" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/10 border border-secondary/20 text-secondary text-sm font-mono mb-6">
            <Layers className="size-4" />
            <span>Our Methodology</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 font-sans">
            The FocusPath Process
          </h2>
          <p className="text-lg text-muted-foreground">
            A structured, four-phase approach to understanding your business challenges and identifying areas
            of opportunity for agentic solutions in your enterprise environment.
          </p>
        </div>

        <div className="relative">
          {}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px -translate-x-1/2 bg-border/50">
            <div className="absolute top-0 left-0 w-full h-1/3 bg-gradient-to-b from-background via-secondary/50 to-transparent" />
            <div className="absolute bottom-0 left-0 w-full h-1/3 bg-gradient-to-t from-background via-secondary/50 to-transparent" />
          </div>

          <div className="space-y-12 md:space-y-24">
            {stages.map((stage, i) => {
            const isEven = i % 2 === 0;
            return <div key={stage.id} data-index={i} className="relative flex flex-col md:flex-row items-start md:items-center group">
                  {}
                  <div className="absolute left-8 md:left-1/2 w-4 h-4 rounded-sm border-2 border-secondary bg-background -translate-x-1/2 mt-6 md:mt-0 z-20 rotate-45 transition-transform duration-500 group-hover:rotate-90 group-hover:bg-secondary shadow-[0_0_15px_var(--color-secondary)]" />

                  {}
                  <div className={`w-full pl-20 md:pl-0 md:w-[calc(50%-3rem)] ${isEven ? "md:mr-auto" : "md:ml-auto"}`}>
                    <Card className="relative overflow-hidden bg-background/40 backdrop-blur-xl border-border/50 shadow-2xl rounded-lg border-l-4 border-l-secondary transition-all duration-300 hover:bg-background/60 hover:border-border">
                      {}
                      <div className="absolute -top-6 -right-4 text-8xl md:text-9xl font-mono font-black text-transparent opacity-20 pointer-events-none select-none z-0" style={{
                    WebkitTextStroke: "2px var(--color-secondary)"
                  }}>
                        {stage.id}
                      </div>

                      <div className="relative z-10">
                        <CardHeader className="pb-4">
                          <div className="flex items-center gap-4 mb-2">
                            <span className="text-secondary font-mono text-sm font-bold tracking-wider">
                              PHASE {stage.id}
                            </span>
                          </div>
                          <CardTitle className="text-2xl md:text-3xl font-sans">
                            {stage.title}
                          </CardTitle>
                          <CardDescription className="text-base mt-2 text-muted-foreground/80">
                            {stage.description}
                          </CardDescription>
                        </CardHeader>

                        <CardContent>
                          <Collapsible className="w-full group/collapsible">
                            <CollapsibleTrigger asChild>
                              <Button variant="outline" className="w-full justify-between rounded-md border-border/50 bg-background/50 hover:bg-accent hover:text-accent-foreground font-mono text-xs uppercase tracking-widest">
                                View Deliverables
                                <ChevronDown className="size-4 transition-transform duration-300 group-data-[state=open]/collapsible:rotate-180" />
                              </Button>
                            </CollapsibleTrigger>
                            <CollapsibleContent className="pt-4 space-y-2 overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
                              {stage.deliverables.map((item, index) => <div key={index} data-index={index} className="flex items-start gap-3 p-3 bg-secondary/10 border border-border/30 rounded-md">
                                  <CircleCheck className="size-4 text-secondary shrink-0 mt-0.5" />
                                  <span className="text-sm text-secondary-foreground">
                                    {item}
                                  </span>
                                </div>)}
                            </CollapsibleContent>
                          </Collapsible>
                        </CardContent>
                      </div>
                    </Card>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}